import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useRegisterRecruiter } from '@/features/auth/hooks/useAuth'
import { passwordSchema, phoneSchema } from '@/features/auth/schemas'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z
  .object({
    organization_name: z.string().min(1, 'Company name is required').max(255),
    website: z.union([z.url('Enter a valid URL'), z.literal('')]).optional(),
    industry: z.string().max(100).optional(),
    name: z.string().min(1, 'Your name is required').max(255),
    email: z.email('Enter a valid email address'),
    phone: phoneSchema,
    password: passwordSchema,
    password_confirmation: z.string(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

type FormValues = z.infer<typeof schema>

export function RegisterRecruiterPage() {
  const navigate = useNavigate()
  const register = useRegisterRecruiter()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      organization_name: '',
      website: '',
      industry: '',
      name: '',
      email: '',
      phone: '',
      password: '',
      password_confirmation: '',
    },
  })

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    register.mutate(
      { ...values, website: values.website || undefined, industry: values.industry || undefined },
      {
        onSuccess: (user) => {
          void navigate('/verify-email', { state: { email: user.email } })
        },
        onError: (error) => setApiError(error),
      },
    )
  })

  return (
    <AuthLayout title="Register Your Company" subtitle="Start hiring on Job Portal" wide>
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput control={control} name="organization_name" label="Company Name" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="website" label="Website" type="url" />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="industry" label="Industry" />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="name" label="Your Name" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="email" label="Work Email" type="email" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="phone" label="Phone" type="tel" required />
          </div>
          <div className="col-md-6">
            <FormInput control={control} name="password" label="Password" type="password" required />
          </div>
          <div className="col-md-6">
            <FormInput
              control={control}
              name="password_confirmation"
              label="Confirm Password"
              type="password"
              required
            />
          </div>
          <div className="col-12">
            <Button type="submit" className="w-100" loading={register.isPending}>
              Register Company
            </Button>
          </div>
        </div>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0">
          Already have an account?{' '}
          <Link to="/login" className="text-primary">
            Sign In
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}
